/**
 * The rates a worker carries on an engagement, built from the rates their profile holds.
 *
 * A profile rate carries its own rate type, and no two rates on one profile share one, so selecting a
 * rate onto a worker is a copy rather than an allocation: the type comes with the rate and the engagement
 * decides only whether it bills.
 */
import type { WorkerPayRate } from '@/types/api/engagement'
import type { ProfilePayRate } from '@/types/api/people'
import { RATE_UNIT } from '@/lib/constants/work-order-codes'

/** The rate type the base rate holds, and the one every engagement already used before this. */
export const BASE_RATE_TYPE_CODE = 'Primary'

/**
 * Whether a rate is the worker's Primary rate, which every placement holds. A profile rate with no type is
 * the rate lifted from a profile that predates rate types, and that rate is Primary.
 */
export const isBaseRateType = (rateTypeCode: string | null | undefined): boolean =>
  !rateTypeCode || rateTypeCode.toLowerCase() === BASE_RATE_TYPE_CODE.toLowerCase()

const sameProfileRate = (rate: WorkerPayRate, profileRateId: string | null | undefined): boolean =>
  !!profileRateId && !!rate.profileRateId && rate.profileRateId.toLowerCase() === profileRateId.toLowerCase()

/**
 * A rate as the worker carries it, which always has a type and a billability, so neither is optional on
 * the way out even though both are on the stored shape.
 */
export type AllocatedPayRate = WorkerPayRate & { rateTypeCode: string; billable: boolean }

const rateFromProfile = (
  profileRate: ProfilePayRate,
  rateTypeCode: string,
  billable: boolean,
): AllocatedPayRate => ({
  profileRateId: profileRate.Id ?? null,
  rateTypeCode,
  payRate: profileRate.PayRate ?? null,
  // A pay rate always has a unit, and Hour is what the engagement defaults to everywhere else. Without
  // this a profile rate saved with no unit reads as a bare amount with nothing after it.
  payRateUnitCode: profileRate.RateUnitCode ?? RATE_UNIT.HOUR,
  description: profileRate.Description ?? null,
  scheduledPayRate: profileRate.ScheduledPayRate ?? null,
  scheduledPayRateEffectiveDate: profileRate.ScheduledPayRateEffectiveDate ?? null,
  billable,
})


/**
 * The worker's rates after a change of selection, each carrying the type, amount and description its
 * profile rate currently has. `selected` is in the order the rates should read.
 */
export const allocateRateSlots = (selected: readonly ProfilePayRate[]): AllocatedPayRate[] =>
  selected.map(profileRate => {
    // A profile that predates rate types carries none on its lifted rate, and that rate is the ordinary
    // hourly one, which is what Primary is.
    const rateTypeCode = profileRate.RateTypeCode || BASE_RATE_TYPE_CODE

    // Billability comes with the rate and is not the engagement's to decide, so what the worker already
    // held is not consulted: the profile is the answer even when it has changed since they were placed.
    return rateFromProfile(profileRate, rateTypeCode, profileRate.Billable !== false)
  })

/**
 * The worker's selection as profile rates, in the order they read, ready to be added to or removed from
 * and handed back to {@link allocateRateSlots}.
 *
 * A rate the profile no longer offers keeps the values the worker holds, so deselecting a different rate
 * cannot quietly change or drop it.
 */
export const selectedProfileRates = (
  selected: readonly WorkerPayRate[],
  profileRates: readonly ProfilePayRate[],
): ProfilePayRate[] =>
  selected.map(rate => {
    const offered = profileRates.find(profileRate => sameProfileRate(rate, profileRate.Id))

    return offered ?? {
      Id: rate.profileRateId ?? undefined,
      RateTypeCode: rate.rateTypeCode ?? undefined,
      Description: rate.description ?? undefined,
      PayRate: rate.payRate ?? undefined,
      RateUnitCode: rate.payRateUnitCode ?? undefined,
      ScheduledPayRate: rate.scheduledPayRate ?? undefined,
      ScheduledPayRateEffectiveDate: rate.scheduledPayRateEffectiveDate ?? undefined,
    }
  })

/**
 * The profile rates behind a selection a worker held before, in the order they read, each as the profile
 * has it now. A rate the profile no longer offers is dropped.
 *
 * Unlike {@link selectedProfileRates}, which keeps an unmatched rate so editing a live selection cannot
 * lose it, this is for bringing a worker back onto rates whose amounts may have moved since.
 */
export const heldProfileRates = (
  held: readonly WorkerPayRate[] | null | undefined,
  profileRates: readonly ProfilePayRate[],
): ProfilePayRate[] =>
  (held ?? []).flatMap(rate => {
    const offered = profileRates.find(profileRate => sameProfileRate(rate, profileRate.Id))

    return offered ? [offered] : []
  })

/**
 * The profile rates a worker could still be given, which is what the picker offers.
 */
export const unselectedProfileRates = (
  selected: readonly WorkerPayRate[] | null | undefined,
  profileRates: readonly ProfilePayRate[],
): ProfilePayRate[] => {
  const held = selected ?? []

  return profileRates.filter(profileRate => {
    // A rate with no id is one the profile has never stored. Nothing can identify it, so it cannot be
    // selected onto a worker or matched again afterwards.
    if (!profileRate.Id) {
      return false
    }

    return !held.some(rate => sameProfileRate(rate, profileRate.Id))
  })
}

/**
 * The rates to show for a worker.
 *
 * Takes a structural shape rather than the worker form type, because the form schema imports this module
 * and the reverse import would be a cycle.
 */
export const payRateRows = (worker: {
  payRates?: readonly WorkerPayRate[] | null
}): WorkerPayRate[] => [...(worker.payRates ?? [])]
